import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const features = [
  {
    icon: '⚡',
    title: 'Lightning Fast',
    desc: 'Redirects happen in milliseconds. Your links never slow down.',
  },
  {
    icon: '📊',
    title: 'Click Analytics',
    desc: 'See exactly how many times your link was clicked and when.',
  },
  {
    icon: '🔒',
    title: 'Secure & Reliable',
    desc: 'Your links are safe with JWT-protected accounts and MongoDB.',
  },
  {
    icon: '✨',
    title: 'Custom Codes',
    desc: 'Choose your own short code like /my-brand instead of random chars.',
  },
];

const steps = [
  { step: '01', title: 'Paste your long URL', desc: 'Any URL, no matter how long or complex.' },
  { step: '02', title: 'Pick a short code', desc: 'Auto-generate or type your own memorable code.' },
  { step: '03', title: 'Share everywhere', desc: 'Copy your short link and share it anywhere.' },
];

export default function Home() {
  const { isAuthenticated } = useAuth();

  return (
    <main className="min-h-screen">

      {/* ─── Hero ───────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 text-white">
        {/* Decorative blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-8 backdrop-blur-sm">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            Free URL shortener - no credit card required
          </div>

          <h1 className="text-5xl md:text-7xl font-black leading-tight tracking-tight mb-6">
            Shorten Links.
            <br />
            <span className="text-brand-200">Track Clicks.</span>
          </h1>

          <p className="text-lg md:text-xl text-brand-200 max-w-xl mx-auto mb-10 leading-relaxed">
            BitLinks turns your long, ugly URLs into clean, memorable short links - with real-time analytics built in.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shorten"
              className="px-8 py-4 bg-white text-brand-700 font-bold rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 text-base">
              Shorten a URL →
            </Link>
            {!isAuthenticated && (
              <Link to="/register"
                className="px-8 py-4 bg-white/10 border border-white/30 text-white font-bold rounded-2xl hover:bg-white/20 transition-all duration-200 text-base backdrop-blur-sm">
                Create Free Account
              </Link>
            )}
            {isAuthenticated && (
              <Link to="/dashboard"
                className="px-8 py-4 bg-white/10 border border-white/30 text-white font-bold rounded-2xl hover:bg-white/20 transition-all duration-200 text-base backdrop-blur-sm">
                My Dashboard →
              </Link>
            )}
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-8 max-w-md mx-auto">
            {[['10K+', 'Links Created'], ['99.9%', 'Uptime'], ['< 50ms', 'Redirect Speed']].map(([val, label]) => (
              <div key={label}>
                <div className="text-2xl font-black text-white">{val}</div>
                <div className="text-xs text-brand-300 mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Features ───────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
            Everything you need to manage links
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Built with the MERN stack - MongoDB, Express, React, and Node.js.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map(({ icon, title, desc }) => (
            <div key={title} className="card hover:shadow-md hover:-translate-y-1 transition-all duration-200 group">
              <div className="text-3xl mb-4">{icon}</div>
              <h3 className="font-bold text-gray-900 mb-2 group-hover:text-brand-600 transition-colors">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── How It Works ───────────────────────────────────────────────────── */}
      <section className="bg-brand-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">How it works</h2>
            <p className="text-gray-500 text-lg">Three simple steps to a shorter link.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map(({ step, title, desc }) => (
              <div key={step} className="text-center">
                <div className="w-14 h-14 bg-brand-600 text-white text-xl font-black rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  {step}
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ────────────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-3xl p-12 text-white shadow-2xl">
          <h2 className="text-3xl md:text-4xl font-black mb-4">Ready to shorten your first link?</h2>
          <p className="text-brand-200 mb-8 text-lg">No signup required to get started. Create an account to unlock the dashboard.</p>
          <Link to="/shorten" className="inline-block px-10 py-4 bg-white text-brand-700 font-black rounded-2xl hover:-translate-y-0.5 hover:shadow-xl transition-all duration-200 text-base">
            Try it now — it's free →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 text-center text-sm text-gray-400">
        <p>© 2026 BitLinks · Aditya Kadam</p>
      </footer>
    </main>
  );
}
